import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import withdrawalService from "../../../services/api/admin.withdrawal.service.js";
import { debounce } from "lodash";
import {
  Eye,
  User,
  DollarSign,
  Calendar,
  Banknote,
  Landmark,
  Wallet,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react"; // Import necessary icons

// Custom hook for error handling
const useApiError = () => {
  const [error, setError] = useState(null);

  const handleError = useCallback((err, defaultMessage) => {
    const errorMessage = err?.message || defaultMessage;
    setError(errorMessage);
    if (errorMessage) {
      toast.error(errorMessage, { toastId: errorMessage });
    }
  }, []);

  return { error, setError, handleError };
};

// Helper function for currency formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

// Reusable Withdrawal Request Row Component
const WithdrawalRequestRow = ({ request, onAction }) => {
  const status = useMemo(() => {
    const statuses = {
      PENDING: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      APPROVED: { color: "bg-green-100 text-green-800", text: "Approved" },
      REJECTED: { color: "bg-red-100 text-red-800", text: "Rejected" },
      COMPLETED: { color: "bg-blue-100 text-blue-800", text: "Completed" },
      FAILED: { color: "bg-pink-100 text-pink-800", text: "Failed" },
    };
    return (
      statuses[request.status] || {
        color: "bg-gray-100 text-gray-800",
        text: request.status,
      }
    );
  }, [request.status]);

  const methodDetails = useMemo(() => {
    if (request.withdrawalMethod === "UPI")
      return `UPI: ${request.upiId || "N/A"}`;
    if (request.withdrawalMethod === "BANK" && request.bankAccount)
      return `Bank: A/C ••••${request.bankAccount.accountNumber?.slice(-4)}`;
    return request.withdrawalMethod;
  }, [request.withdrawalMethod, request.upiId, request.bankAccount]);

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {request.user?.name || "N/A"}
        </div>
        <div className="text-sm text-gray-500">
          {request.user?.email || "No email"}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-semibold">
        ₹{request.amount.toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {methodDetails}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(request.requestedAt)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 inline-flex text-xs font-semibold rounded-full ${status.color}`}
        >
          {status.text}
        </span>
      </td>
      <td className="py-4 whitespace-nowrap text-center text-sm font-medium">
        <div className="flex justify-center gap-4">
          <button
            onClick={() => onAction(request, "view")}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <Eye className="w-4 h-4" /> {/* Eye icon for view */}
          </button>
          {request.status === "PENDING" && (
            <>
              <button
                onClick={() => onAction(request, "approve")}
                className="text-green-600 hover:text-green-900"
                title="Approve Request"
              >
                <CheckCircle className="w-4 h-4" />{" "}
                {/* Check icon for approve */}
              </button>
              <button
                onClick={() => onAction(request, "reject")}
                className="text-red-600 hover:text-red-900"
                title="Reject Request"
              >
                <XCircle className="w-4 h-4" /> {/* X icon for reject */}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Reusable Modal Component for Approve/Reject Actions
const ActionModal = ({
  isOpen,
  onClose,
  request,
  actionType,
  detailedRequest,
  loadingDetails,
  onConfirm,
  isProcessing,
}) => {
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    // Reset rejection reason when modal opens or selected request changes
    if (isOpen) {
      setRejectionReason("");
    }
  }, [isOpen, request]);

  if (!isOpen || !request) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4 capitalize">
          {actionType} Withdrawal Request
        </h3>
        <div className="text-sm text-gray-600">
          <p className="mb-4">
            You are about to <strong>{actionType}</strong> this withdrawal
            request.
          </p>
          <div className="bg-gray-50 p-4 rounded-md space-y-2 mb-4 border border-gray-200">
            {loadingDetails ? (
              <p className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin text-gray-500" />
                Loading details...
              </p>
            ) : detailedRequest ? (
              <>
                <p>
                  <strong>User:</strong> {detailedRequest.user?.name || "N/A"} (
                  {detailedRequest.user?.email || "No email"})
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {formatCurrency(detailedRequest.amount || 0)}
                </p>
                <p>
                  <strong>Method:</strong>{" "}
                  {detailedRequest.withdrawalMethod || "N/A"}
                </p>
                {detailedRequest.withdrawalMethod === "UPI" && (
                  <p>
                    <strong>UPI ID:</strong> {detailedRequest.upiId || "N/A"}
                  </p>
                )}
                {detailedRequest.withdrawalMethod === "BANK" &&
                  detailedRequest.bankAccount && (
                    <>
                      <p>
                        <strong>Bank:</strong>{" "}
                        {detailedRequest.bankAccount.bankName || "N/A"}
                      </p>
                      <p>
                        <strong>A/C Holder:</strong>{" "}
                        {detailedRequest.bankAccount.accountHolderName || "N/A"}
                      </p>
                      <p>
                        <strong>A/C Number:</strong>{" "}
                        {detailedRequest.bankAccount.accountNumber || "N/A"}
                      </p>
                      <p>
                        <strong>IFSC:</strong>{" "}
                        {detailedRequest.bankAccount.ifscCode || "N/A"}
                      </p>
                    </>
                  )}
              </>
            ) : (
              <p>No details available.</p>
            )}
          </div>
          {actionType === "reject" && (
            <div>
              <label
                htmlFor="rejectionReason"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rejectionReason"
                rows="3"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="e.g., Incorrect bank details, KYC not complete..."
                required
              />
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(rejectionReason)}
            disabled={
              isProcessing ||
              (actionType === "reject" && !rejectionReason.trim())
            }
            className={`rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 ${
              actionType === "approve"
                ? "bg-green-600 hover:bg-green-700 focus:ring-green-500 disabled:bg-green-300"
                : "bg-red-600 hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300"
            }`}
          >
            {isProcessing ? "Processing..." : `Confirm ${actionType}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// New Component for View Details Modal
const ViewDetailsModal = ({
  isOpen,
  onClose,
  detailedRequest,
  loadingDetails,
  onApproveReject, // Pass down handler for approve/reject buttons inside view modal
}) => {
  if (!isOpen || !detailedRequest) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Withdrawal Request Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {loadingDetails ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 mr-2 animate-spin text-indigo-600" />
            <span className="text-gray-600">Loading full details...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
            {/* Request Information */}
            <div className="bg-gray-50 p-4 rounded-md shadow-sm space-y-2">
              <h4 className="font-semibold text-gray-800 flex items-center mb-2">
                <Banknote className="w-4 h-4 mr-2 text-indigo-600" /> Request
                Details
              </h4>
              <p>
                <strong>ID:</strong> {detailedRequest._id || "N/A"}
              </p>
              <p>
                <strong>Amount:</strong>{" "}
                {formatCurrency(detailedRequest.amount || 0)}
              </p>
              <p>
                <strong>Method:</strong>{" "}
                {detailedRequest.withdrawalMethod || "N/A"}
              </p>
              {detailedRequest.withdrawalMethod === "UPI" && (
                <p>
                  <strong>UPI ID:</strong> {detailedRequest.upiId || "N/A"}
                </p>
              )}
              {detailedRequest.withdrawalMethod === "BANK" &&
                detailedRequest.bankAccount && (
                  <>
                    <p>
                      <strong>Bank:</strong>{" "}
                      {detailedRequest.bankAccount.bankName || "N/A"}
                    </p>
                    <p>
                      <strong>A/C Holder:</strong>{" "}
                      {detailedRequest.bankAccount.accountHolderName || "N/A"}
                    </p>
                    <p>
                      <strong>A/C Number:</strong>{" "}
                      {detailedRequest.bankAccount.accountNumber || "N/A"}
                    </p>
                    <p>
                      <strong>IFSC:</strong>{" "}
                      {detailedRequest.bankAccount.ifscCode || "N/A"}
                    </p>
                  </>
                )}
              <p>
                <strong>Requested At:</strong>{" "}
                {detailedRequest.requestedAt
                  ? new Date(detailedRequest.requestedAt).toLocaleString()
                  : "N/A"}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                    detailedRequest.status === "PENDING"
                      ? "bg-yellow-100 text-yellow-800"
                      : detailedRequest.status === "APPROVED"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {detailedRequest.status}
                </span>
              </p>
              {detailedRequest.status === "REJECTED" &&
                detailedRequest.reason && (
                  <p>
                    <strong>Rejection Reason:</strong> {detailedRequest.reason}
                  </p>
                )}
            </div>

            {/* User Information */}
            <div className="bg-gray-50 p-4 rounded-md shadow-sm space-y-2">
              <h4 className="font-semibold text-gray-800 flex items-center mb-2">
                <User className="w-4 h-4 mr-2 text-indigo-600" /> User Details
              </h4>
              <p>
                <strong>Name:</strong> {detailedRequest.user?.name || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {detailedRequest.user?.email || "N/A"}
              </p>
              <p>
                <strong>Mobile:</strong>{" "}
                {detailedRequest.user?.mobileNo || "N/A"}
              </p>
              <p>
                <strong>Wallet Balance:</strong>{" "}
                {formatCurrency(detailedRequest.user?.walletBalance || 0)}
              </p>
              <p>
                <strong>KYC Verified:</strong>{" "}
                {detailedRequest.user?.isKycVerified ? (
                  <span className="text-green-600">Yes</span>
                ) : (
                  <span className="text-red-600">No</span>
                )}
              </p>
              {/* Add more user details if available in detailedRequest.user */}
            </div>
          </div>
        )}

        {/* Action Buttons for Pending Requests in View Modal */}
        {detailedRequest.status === "PENDING" && (
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => onApproveReject(detailedRequest, "approve")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              disabled={loadingDetails}
            >
              Approve
            </button>
            <button
              onClick={() => onApproveReject(detailedRequest, "reject")}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={loadingDetails}
            >
              Reject
            </button>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminWalletManagement = () => {
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [filterStatus, setFilterStatus] = useState("PENDING");
  const [modalState, setModalState] = useState({
    isOpen: false, // For Approve/Reject Modal
    selectedRequest: null,
    actionType: null,
    detailedRequest: null,
    loadingDetails: false,
    isProcessingAction: false,
  });
  const [viewModalOpen, setViewModalOpen] = useState(false); // State for View Details Modal
  const { error, setError, handleError } = useApiError();

  // Debounced fetch to prevent rapid API calls
  const fetchWithdrawalRequests = useCallback(
    debounce(async (page, status) => {
      setLoading(true);
      setError(null);
      try {
        const response = await withdrawalService.getAllWithdrawalRequests({
          page,
          limit: pagination.limit,
          status: status === "ALL" ? undefined : status,
          sortBy: "requestedAt",
          sortOrder: "desc",
        });

        if (response?.success) {
          const data = response.data;
          const requestsArray = data?.data || data?.requests || [];
          const paginationData = data?.pagination || {};

          setWithdrawalRequests(
            Array.isArray(requestsArray) ? requestsArray : []
          );
          setPagination((prev) => ({
            ...prev,
            page: paginationData.current || paginationData.currentPage || page,
            total:
              paginationData.totalRecords || paginationData.totalRequests || 0,
            totalPages: paginationData.total || paginationData.totalPages || 1,
            hasNext: paginationData.hasNext || false,
            hasPrev: paginationData.hasPrev || false,
          }));
        } else {
          if (response?.error) {
            handleError(null, response.error);
          }
          setWithdrawalRequests([]);
        }
      } catch (err) {
        handleError(err, "Failed to fetch withdrawal requests");
        setWithdrawalRequests([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [pagination.limit, handleError]
  );

  // Fetch detailed request - now also used for the separate View Details Modal
  const fetchWithdrawalRequestDetails = useCallback(
    async (requestId, actionToPerform = null) => {
      setModalState((prev) => ({
        ...prev,
        loadingDetails: true,
        detailedRequest: null,
      })); // Clear detailedRequest before fetching
      try {
        const response = await withdrawalService.getWithdrawalRequestById(
          requestId
        );
        if (response?.success) {
          setModalState((prev) => ({
            ...prev,
            detailedRequest: response.data,
          }));
          if (actionToPerform === "view") {
            setViewModalOpen(true); // Open the dedicated view modal
          } else {
            // If action is approve/reject, set the request for the action modal
            setModalState((prev) => ({
              ...prev,
              selectedRequest: response.data,
              isOpen: true,
              actionType: actionToPerform,
            }));
          }
        } else {
          toast.error(
            response?.error || "Failed to fetch withdrawal request details",
            { toastId: "details-fetch-error" }
          );
        }
      } catch (err) {
        toast.error("An error occurred while fetching request details", {
          toastId: "details-fetch-exception",
        });
      } finally {
        setModalState((prev) => ({ ...prev, loadingDetails: false }));
      }
    },
    []
  );

  // Fetch on mount or filter/page change
  useEffect(() => {
    fetchWithdrawalRequests(pagination.page, filterStatus);
  }, [pagination.page, filterStatus, fetchWithdrawalRequests]);

  // Handle opening of modals based on action type
  const handleOpenModal = useCallback(
    (request, actionType) => {
      // Close any open modals first
      setModalState((prev) => ({
        ...prev,
        isOpen: false,
        selectedRequest: null,
        actionType: null,
      }));
      setViewModalOpen(false);

      if (actionType === "view") {
        fetchWithdrawalRequestDetails(request._id, "view");
      } else {
        // For approve/reject, fetch details and then open the ActionModal
        fetchWithdrawalRequestDetails(request._id, actionType);
      }
    },
    [fetchWithdrawalRequestDetails]
  );

  const handleCloseActionModal = useCallback(() => {
    if (modalState.isProcessingAction) return;
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
      selectedRequest: null,
      actionType: null,
      detailedRequest: null,
    }));
  }, [modalState.isProcessingAction]);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setModalState((prev) => ({
      ...prev,
      selectedRequest: null, // Clear selected request after closing view modal
      detailedRequest: null,
    }));
  }, []);

  const handleConfirmAction = useCallback(
    async (rejectionReason) => {
      if (!modalState.selectedRequest || !modalState.actionType) return;
      if (modalState.actionType === "reject" && !rejectionReason.trim()) {
        toast.error("Rejection reason is required.", {
          toastId: "rejection-reason-required",
        });
        return;
      }

      setModalState((prev) => ({ ...prev, isProcessingAction: true }));
      try {
        const response =
          modalState.actionType === "approve"
            ? await withdrawalService.approveWithdrawalRequest(
                modalState.selectedRequest._id
              )
            : await withdrawalService.rejectWithdrawalRequest(
                modalState.selectedRequest._id,
                rejectionReason
              );

        if (response.success) {
          toast.success(`Request ${modalState.actionType}d successfully.`, {
            toastId: `action-success-${modalState.selectedRequest._id}`,
          });
          handleCloseActionModal();
          fetchWithdrawalRequests(pagination.page, filterStatus);
        } else {
          handleError(
            null,
            response.error || `Failed to ${modalState.actionType} request.`
          );
        }
      } catch (err) {
        handleError(
          err,
          `An error occurred during the ${modalState.actionType} process.`
        );
      } finally {
        setModalState((prev) => ({ ...prev, isProcessingAction: false }));
      }
    },
    [
      modalState.selectedRequest,
      modalState.actionType,
      handleError,
      fetchWithdrawalRequests,
      pagination.page,
      filterStatus,
      handleCloseActionModal,
    ]
  );

  // This function will be passed to ViewDetailsModal to allow approving/rejecting directly from it
  const handleApproveRejectFromView = useCallback((request, actionType) => {
    setViewModalOpen(false); // Close the view modal
    setModalState((prev) => ({
      ...prev,
      isOpen: true,
      selectedRequest: request,
      actionType: actionType,
      detailedRequest: request, // Use the already fetched detailed request
      loadingDetails: false,
    }));
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">
        Withdrawal Request Management
      </h2>

      {/* Filters and Controls */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <label
            htmlFor="statusFilter"
            className="text-sm font-medium text-gray-700"
          >
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="block w-40 pl-3 pr-10 py-2 text-base border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            {[
              "ALL",
              "PENDING",
              "APPROVED",
              "REJECTED",
              "COMPLETED",
              "FAILED",
            ].map((status) => (
              <option key={status} value={status}>
                {status.charAt(0) + status.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => fetchWithdrawalRequests(pagination.page, filterStatus)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{error}</div>
      )}

      {/* Requests Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                "User",
                "Amount",
                "Method & Details",
                "Date",
                "Status",
                "Actions",
              ].map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  Loading requests...
                </td>
              </tr>
            ) : withdrawalRequests.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500">
                  No withdrawal requests found.
                </td>
              </tr>
            ) : (
              withdrawalRequests.map((request) => (
                <WithdrawalRequestRow
                  key={request._id}
                  request={request}
                  onAction={handleOpenModal}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={!pagination.hasPrev || loading}
            className="px-4 py-2 border rounded-md text-sm font-medium disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={!pagination.hasNext || loading}
            className="px-4 py-2 border rounded-md text-sm font-medium disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Action Modal (Approve/Reject) */}
      <ActionModal
        isOpen={modalState.isOpen}
        onClose={handleCloseActionModal}
        request={modalState.selectedRequest}
        actionType={modalState.actionType}
        detailedRequest={modalState.detailedRequest}
        loadingDetails={modalState.loadingDetails}
        onConfirm={handleConfirmAction}
        isProcessing={modalState.isProcessingAction}
      />

      {/* View Details Modal (New) */}
      <ViewDetailsModal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        detailedRequest={modalState.detailedRequest}
        loadingDetails={modalState.loadingDetails}
        onApproveReject={handleApproveRejectFromView}
      />
    </div>
  );
};

// PropTypes for type safety
WithdrawalRequestRow.propTypes = {
  request: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    user: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      mobileNo: PropTypes.string, // Added mobileNo to propTypes
      isKycVerified: PropTypes.bool, // Added isKycVerified
      walletBalance: PropTypes.number, // Added walletBalance
    }),
    amount: PropTypes.number.isRequired,
    withdrawalMethod: PropTypes.string.isRequired,
    upiId: PropTypes.string,
    bankAccount: PropTypes.shape({
      accountNumber: PropTypes.string,
      bankName: PropTypes.string,
      accountHolderName: PropTypes.string,
      ifscCode: PropTypes.string,
    }),
    status: PropTypes.string.isRequired,
    requestedAt: PropTypes.string,
    reason: PropTypes.string, // Added reason for rejection/completion notes
  }).isRequired,
  onAction: PropTypes.func.isRequired,
};

ActionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  request: PropTypes.object,
  actionType: PropTypes.string,
  detailedRequest: PropTypes.object,
  loadingDetails: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool.isRequired,
};

ViewDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  detailedRequest: PropTypes.object, // Use object for detailedRequest
  loadingDetails: PropTypes.bool.isRequired,
  onApproveReject: PropTypes.func.isRequired,
};

export default AdminWalletManagement;
